import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import type { OrgMember } from '../types/org-users.types';

interface RemoveMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: OrgMember | null;
  onConfirm: () => Promise<void>;
}

export const RemoveMemberDialog = ({
  open,
  onOpenChange,
  member,
  onConfirm,
}: RemoveMemberDialogProps) => {
  const { t } = useTranslation();
  const [isRemoving, setIsRemoving] = useState(false);

  const memberName = member ? `${member.firstName || ''} ${member.lastName || ''}`.trim() : '';
  const displayName = memberName || member?.email || '';

  const handleConfirm = async () => {
    setIsRemoving(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Error handled in parent
    } finally {
      setIsRemoving(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isRemoving) {
      onOpenChange(newOpen);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-[#fff] w-[95vw] max-w-md max-h-[90dvh] p-4 sm:p-6 rounded-2xl">
        <AlertDialogHeader className="text-left">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-[hsl(var(--crimson))]/10 shrink-0">
              <AlertTriangle className="size-5 text-crimson" />
            </div>
            <AlertDialogTitle className="text-base sm:text-lg">
              {t('ORGTEAM_REMOVEDIALOG_TITLE')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            {t('ORGTEAM_REMOVEDIALOG_DESCRIPTION', { name: displayName })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-row justify-end gap-2">
          <Button
            variant="outline"
            size={'sm'}
            onClick={() => handleOpenChange(false)}
            disabled={isRemoving}
          >
            {t('ORGTEAM_REMOVEDIALOG_ACTIONS_CANCEL')}
          </Button>
          <Button variant="destructive" size={'sm'} onClick={handleConfirm} disabled={isRemoving}>
            {isRemoving && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('ORGTEAM_REMOVEDIALOG_ACTIONS_REMOVE')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
